// src/pages/curriculum/CurriculumManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdCheckCircle,
  MdCancel,
  MdAutoAwesome,
  MdMenuBook,
  MdQuiz,
  MdExpandMore,
  MdExpandLess,
  MdVisibility,
  MdVisibilityOff,
  MdSchool,
  MdRefresh,
  MdViewList,
  MdCalendarToday,
  MdLayers,
  MdArrowForward,
  MdCheck,
  MdPictureAsPdf,
  MdUploadFile,
  MdLock,
  MdLockOpen,
} from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import AIDocumentImporterModal from '../../components/teacher/AIDocumentImporterModal';
import {
  fetchCurriculumTopics,
  saveCurriculumTopic,
  deleteCurriculumTopic,
  fetchCurriculumQuestions,
  saveCurriculumQuestion,
  deleteCurriculumQuestion,
  setQuestionApproval,
  triggerDynamicSeed,
  SYLLABUS_TEMPLATES,
  fetchCustomSubjects,
  saveCustomSubject,
  deleteCustomSubject,
  fetchDeletedSubjectIds,
} from '../../services/curriculumEditorService';

const CLASSES = [
  { id: 'ss1', name: 'SS 1' },
  { id: 'ss2', name: 'SS 2' },
  { id: 'ss3', name: 'SS 3' },
  { id: 'jss1', name: 'JSS 1' },
  { id: 'jss2', name: 'JSS 2' },
  { id: 'jss3', name: 'JSS 3' },
  { id: 'utme', name: 'UTME / JAMB' },
];

export const OFFICIAL_JSS_SUBJECT_IDS = [
  'mathematics',
  'english_studies',
  'phe',
  'crs',
  'irs',
  'nigerian_history',
  'social_citizenship',
  'cca',
  'french',
  'intermediate_science',
  'digital_technologies',
  'business_studies',
  'trade_subjects',
];

const DEFAULT_SUBJECTS = [
  // General & Senior Core
  { id: 'mathematics', name: 'Mathematics', stream: 'general', classes: ['ss1', 'ss2', 'ss3', 'jss1', 'jss2', 'jss3', 'utme'] },
  { id: 'english', name: 'English Language', stream: 'general', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'physics', name: 'Physics', stream: 'science', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'chemistry', name: 'Chemistry', stream: 'science', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'biology', name: 'Biology', stream: 'science', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'economics', name: 'Economics', stream: 'commercial', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'government', name: 'Government', stream: 'art', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'literature', name: 'Literature in English', stream: 'art', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'further_mathematics', name: 'Further Mathematics', stream: 'science', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'agric', name: 'Agricultural Science', stream: 'general', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'accounting', name: 'Financial Accounting', stream: 'commercial', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'marketing', name: 'Marketing', stream: 'commercial', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'civic', name: 'Civic Education', stream: 'general', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'commerce', name: 'Commerce', stream: 'commercial', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
  { id: 'geography', name: 'Geography', stream: 'general', classes: ['ss1', 'ss2', 'ss3', 'utme'] },

  // Official 13 Junior Secondary (JSS) Subjects
  { id: 'english_studies', name: 'English Studies', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'phe', name: 'Physical and Health Education', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'crs', name: 'Christian Religious Studies', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'irs', name: 'Islamic Studies', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'nigerian_history', name: 'Nigerian History', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'social_citizenship', name: 'Social and Citizenship Studies', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'cca', name: 'Cultural and Creative Arts (CCA)', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'french', name: 'French', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'intermediate_science', name: 'Intermediate Science', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'digital_technologies', name: 'Digital Technologies', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'business_studies', name: 'Business Studies', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },
  { id: 'trade_subjects', name: 'Trade Subjects', stream: 'general', classes: ['jss1', 'jss2', 'jss3'] },

  // Legacy / compatibility aliases (archived from JSS)
  { id: 'basic_science', name: 'Basic Science (Legacy)', stream: 'general', classes: [] },
  { id: 'basic_tech', name: 'Basic Technology (Legacy)', stream: 'general', classes: [] },
  { id: 'social_studies', name: 'Social Studies (Legacy)', stream: 'general', classes: [] },
  { id: 'ict', name: 'ICT / Computer Studies (Legacy)', stream: 'general', classes: ['ss1', 'ss2', 'ss3', 'utme'] },
];

const CurriculumManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile, isAdmin } = useAuth();
  const [selectedClass, setSelectedClass] = useState('ss1');
  const [selectedSubject, setSelectedSubject] = useState('mathematics');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'syllabus'); // 'syllabus', 'topics', or 'questions'
  const [showClassOverview, setShowClassOverview] = useState(false);
  const [isDocImporterOpen, setIsDocImporterOpen] = useState(false);

  // Dynamic Subjects & Stream Filter state
  const [customSubjects, setCustomSubjects] = useState([]);
  const [deletedSubjectIds, setDeletedSubjectIds] = useState([]);
  const [selectedStreamFilter, setSelectedStreamFilter] = useState('all'); // 'all', 'science', 'commercial', 'art', 'general'
  const [isAddSubjectModalOpen, setIsAddSubjectModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null); // null if creating, or subject object if editing
  const [isSavingSubject, setIsSavingSubject] = useState(false);
  const [newSubjectForm, setNewSubjectForm] = useState({
    name: '',
    id: '',
    idManual: false,
    stream: 'general',
    classes: ['ss1', 'ss2', 'ss3'],
  });

  // Load custom subjects and deleted subject IDs from Firestore & local cache
  useEffect(() => {
    const loadSubjects = async () => {
      try {
        const [customData, deletedData] = await Promise.all([
          fetchCustomSubjects(),
          fetchDeletedSubjectIds(),
        ]);
        if (Array.isArray(customData)) {
          setCustomSubjects(customData);
        }
        if (Array.isArray(deletedData)) {
          setDeletedSubjectIds(deletedData);
        }
      } catch (err) {
        console.warn('Could not load subjects or deleted subjects:', err);
      }
    };
    loadSubjects();
  }, []);

  // Merge default subjects and custom subjects, filtering out deleted ones
  const allSubjects = useMemo(() => {
    const merged = [];

    // 1. Add default subjects if not deleted
    DEFAULT_SUBJECTS.forEach((ds) => {
      if (!deletedSubjectIds.includes(ds.id)) {
        merged.push({ ...ds });
      }
    });

    // 2. Merge custom subjects or overrides
    if (Array.isArray(customSubjects)) {
      customSubjects.forEach((cs) => {
        if (!deletedSubjectIds.includes(cs.id)) {
          const existingIdx = merged.findIndex((s) => s.id === cs.id);
          if (existingIdx >= 0) {
            merged[existingIdx] = {
              ...merged[existingIdx],
              ...cs,
              isCustom: true,
            };
          } else {
            merged.push({
              id: cs.id,
              name: cs.name,
              stream: cs.stream || 'general',
              classes: cs.classes || ['ss1', 'ss2', 'ss3'],
              isCustom: true,
            });
          }
        }
      });
    }
    return merged;
  }, [customSubjects, deletedSubjectIds]);

  // Keep SUBJECTS reference identical so all existing handlers continue working seamlessly
  const SUBJECTS = allSubjects;

  const isJuniorClass = selectedClass === 'jss1' || selectedClass === 'jss2' || selectedClass === 'jss3';

  // Filter subjects based on selected class and stream
  const availableSubjectsForClass = useMemo(() => {
    const filtered = allSubjects.filter((subj) => {
      if (isJuniorClass) {
        // Under JSS classes, strictly display only the 13 approved curriculum subjects
        if (['civic', 'basic_science', 'basic_tech', 'social_studies', 'ict'].includes(subj.id)) {
          return false;
        }
        if (OFFICIAL_JSS_SUBJECT_IDS.includes(subj.id)) {
          return true;
        }
        // If a new custom subject was created explicitly for this JSS class (excluding legacy/senior)
        if (subj.classes && subj.classes.includes(selectedClass)) {
          return true;
        }
        return false;
      }

      if (subj.classes && subj.classes.length > 0) {
        if (!subj.classes.includes(selectedClass)) return false;
      }

      if (!isJuniorClass && selectedStreamFilter !== 'all') {
        if (subj.stream !== selectedStreamFilter && subj.stream !== 'general') {
          return false;
        }
      }
      return true;
    });

    if (isJuniorClass) {
      return [...filtered].sort((a, b) => {
        const indexA = OFFICIAL_JSS_SUBJECT_IDS.indexOf(a.id);
        const indexB = OFFICIAL_JSS_SUBJECT_IDS.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.name.localeCompare(b.name);
      });
    }

    return filtered;
  }, [allSubjects, selectedClass, isJuniorClass, selectedStreamFilter]);

  // Keep selectedSubject valid for current class
  useEffect(() => {
    if (availableSubjectsForClass.length > 0) {
      const exists = availableSubjectsForClass.some((s) => s.id === selectedSubject);
      if (!exists) {
        setSelectedSubject(availableSubjectsForClass[0].id);
      }
    }
  }, [availableSubjectsForClass, selectedSubject]);

  // Handle opening edit modal for an existing subject
  const handleOpenEditSubjectModal = (subj) => {
    setEditingSubject(subj);
    setNewSubjectForm({
      name: subj.name,
      id: subj.id,
      idManual: true,
      stream: subj.stream || (isJuniorClass ? 'general' : 'science'),
      classes: subj.classes && subj.classes.length > 0 ? subj.classes : [selectedClass],
    });
    setIsAddSubjectModalOpen(true);
  };

  // Handle deleting a subject (removes from Firestore & local list)
  const handleDeleteSubject = async (subj) => {
    if (!window.confirm(`Are you sure you want to delete "${subj.name}"? This will remove it from the curriculum.`)) {
      return;
    }

    try {
      await deleteCustomSubject(subj.id);

      setDeletedSubjectIds((prev) => (prev.includes(subj.id) ? prev : [...prev, subj.id]));
      setCustomSubjects((prev) => prev.filter((s) => s.id !== subj.id));

      setFeedbackMsg(`Subject "${subj.name}" has been deleted from the curriculum.`);

      const remaining = availableSubjectsForClass.filter((s) => s.id !== subj.id);
      if (remaining.length > 0) {
        setSelectedSubject(remaining[0].id);
      }
    } catch (err) {
      alert('Failed to delete subject: ' + err?.message);
    }
  };

  // Handle saving new or updated custom subject
  const handleSaveNewSubject = async (e) => {
    e.preventDefault();
    if (!newSubjectForm.name.trim()) {
      alert('Please enter a subject name');
      return;
    }
    if (!newSubjectForm.classes || newSubjectForm.classes.length === 0) {
      alert('Please select at least one class for this subject');
      return;
    }

    setIsSavingSubject(true);
    try {
      const subjectId = editingSubject
        ? editingSubject.id
        : (newSubjectForm.id.trim() ||
           newSubjectForm.name
             .trim()
             .toLowerCase()
             .replace(/[^a-z0-9]/g, '_')
             .replace(/_+/g, '_')
             .replace(/^_|_$/g, ''));

      const saved = await saveCustomSubject({
        name: newSubjectForm.name.trim(),
        id: subjectId,
        stream: newSubjectForm.stream,
        classes: newSubjectForm.classes,
      });

      // Remove from deleted list if it was previously hidden
      setDeletedSubjectIds((prev) => prev.filter((id) => id !== saved.id));

      setCustomSubjects((prev) => {
        const filtered = prev.filter((s) => s.id !== saved.id);
        return [...filtered, saved];
      });

      setSelectedSubject(saved.id);
      if (!saved.classes.includes(selectedClass)) {
        setSelectedClass(saved.classes[0]);
      }
      setIsAddSubjectModalOpen(false);
      setFeedbackMsg(
        editingSubject
          ? `Subject "${saved.name}" updated successfully!`
          : `Subject "${saved.name}" successfully added to curriculum!`
      );
      setEditingSubject(null);
      setNewSubjectForm({
        name: '',
        id: '',
        idManual: false,
        stream: 'general',
        classes: ['ss1', 'ss2', 'ss3'],
      });
    } catch (err) {
      alert('Failed to save subject: ' + err?.message);
    } finally {
      setIsSavingSubject(false);
    }
  };

  // Topics & Steps state
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [expandedTopicId, setExpandedTopicId] = useState(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);

  // Questions state
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questionFilter, setQuestionFilter] = useState('all'); // 'all', 'approved', 'pending'

  // Modals state
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm, setTopicForm] = useState({
    topicId: '',
    title: '',
    term: 'Term 1',
    order: 1,
  });

  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [targetTopicForStep, setTargetTopicForStep] = useState(null);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [stepForm, setStepForm] = useState({
    stepNumber: 1,
    title: '',
    concept: '',
    analogy: '',
    keyRule: '',
    example: '',
    checkQuestionText: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: '',
    explanation: '',
  });

  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    id: '',
    topicId: '',
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: '',
    explanation: '',
    difficulty: 1,
    approved: true,
  });

  const [seedingLoading, setSeedingLoading] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  // Collapsible terms: false = hidden (collapsed), true = visible (expanded)
  const [expandedTerms, setExpandedTerms] = useState({
    'Term 1': false,
    'Term 2': false,
    'Term 3': false,
  });

  const toggleTerm = (termName) => {
    setExpandedTerms((prev) => ({
      ...prev,
      [termName]: !prev[termName],
    }));
  };

  const handleExpandAllTerms = () => {
    setExpandedTerms({ 'Term 1': true, 'Term 2': true, 'Term 3': true });
  };

  const handleCollapseAllTerms = () => {
    setExpandedTerms({ 'Term 1': false, 'Term 2': false, 'Term 3': false });
  };

  const normalizeTerm = (termStr) => {
    if (!termStr) return 'Term 1';
    const l = termStr.toLowerCase();
    if (l.includes('1') || l.includes('first')) return 'Term 1';
    if (l.includes('2') || l.includes('second')) return 'Term 2';
    if (l.includes('3') || l.includes('third')) return 'Term 3';
    return 'Term 1';
  };

  // Group syllabus topics by academic term
  const syllabusByTerm = useMemo(() => {
    const grouped = {
      'Term 1': [],
      'Term 2': [],
      'Term 3': [],
    };

    topics.forEach((topic) => {
      const termKey = normalizeTerm(topic.term);
      if (grouped[termKey]) {
        grouped[termKey].push(topic);
      } else {
        grouped['Term 1'].push(topic);
      }
    });

    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return grouped;
  }, [topics]);

  // Questions count map per topic
  const questionsCountByTopic = useMemo(() => {
    const map = {};
    questions.forEach((q) => {
      if (q.topicId) {
        map[q.topicId] = (map[q.topicId] || 0) + 1;
      }
    });
    return map;
  }, [questions]);

  // Load data when class or subject changes
  useEffect(() => {
    setSelectedTopicIds([]);
    loadTopics();
    loadQuestions();
  }, [selectedClass, selectedSubject]);

  const loadTopics = async () => {
    setLoadingTopics(true);
    try {
      const data = await fetchCurriculumTopics(selectedClass, selectedSubject);
      setTopics(data);
      if (data.length > 0 && !expandedTopicId) {
        setExpandedTopicId(data[0].topicId);
      }
    } catch (err) {
      console.error('Failed to load topics:', err);
    } finally {
      setLoadingTopics(false);
    }
  };

  const loadQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const data = await fetchCurriculumQuestions(selectedClass, selectedSubject);
      setQuestions(data);
    } catch (err) {
      console.error('Failed to load questions:', err);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleQuickSeed = async () => {
    const currentClassName = CLASSES.find((c) => c.id === selectedClass)?.name || selectedClass.toUpperCase();
    const currentSubjectName = SUBJECTS.find((s) => s.id === selectedSubject)?.name || selectedSubject;

    if (!window.confirm(`Auto-generate curriculum topics and practice questions for ${currentClassName} ${currentSubjectName}?`)) return;
    setSeedingLoading(true);
    setFeedbackMsg('');
    try {
      const result = await triggerDynamicSeed({
        classId: selectedClass,
        subjectId: selectedSubject,
        className: currentClassName,
        subjectName: currentSubjectName,
      });
      setFeedbackMsg(`Successfully generated ${result.topicsSeeded} topics and ${result.questionsSeeded} questions for ${currentClassName} ${currentSubjectName}!`);
      loadTopics();
      loadQuestions();
    } catch (err) {
      setFeedbackMsg('Generation failed: ' + err.message);
    } finally {
      setSeedingLoading(false);
    }
  };

  // --- TOPIC & SYLLABUS HANDLERS ---

  const handleOpenTopicModal = (topic = null, defaultTerm = 'Term 1', currentWeek = 1) => {
    if (topic) {
      const explicitWeek = topic.order !== undefined && topic.order !== null ? Number(topic.order) : (currentWeek || 1);
      setEditingTopic({
        ...topic,
        order: explicitWeek,
      });
      setTopicForm({
        topicId: topic.topicId || topic.id,
        title: topic.title,
        term: topic.term || defaultTerm || 'Term 1',
        order: explicitWeek,
      });
    } else {
      setEditingTopic(null);
      const termTopics = topics.filter((t) => normalizeTerm(t.term) === normalizeTerm(defaultTerm));
      setTopicForm({
        topicId: `${selectedClass}_${selectedSubject}_topic_${Date.now()}`,
        title: '',
        term: defaultTerm,
        order: termTopics.length + 1,
      });
    }
    setIsTopicModalOpen(true);
  };

  const handleImportSyllabusTemplate = async (targetTerm = 'Term 1') => {
    const template = SYLLABUS_TEMPLATES[selectedSubject]?.[selectedClass];
    if (!template || template.length === 0) {
      alert(`No static template found for ${selectedClass.toUpperCase()} ${selectedSubject}. Use the "⚡ Auto-Generate with AI" button above!`);
      return;
    }
    if (!window.confirm(`Import ${template.length} NERDC Syllabus topics into ${targetTerm}?`)) return;

    setLoadingTopics(true);
    try {
      const existingInTerm = topics.filter((t) => normalizeTerm(t.term) === normalizeTerm(targetTerm));
      const startOrder = existingInTerm.length + 1;

      for (let i = 0; i < template.length; i++) {
        const item = template[i];
        await saveCurriculumTopic({
          topicId: `${selectedClass}_${selectedSubject}_${item.id}_${Date.now()}_${i}`,
          title: item.title,
          term: targetTerm,
          order: startOrder + i,
          classId: selectedClass,
          subjectId: selectedSubject,
          stepsCount: 0,
          steps: [],
        });
      }
      setFeedbackMsg(`Successfully imported ${template.length} topics from NERDC syllabus into ${targetTerm}!`);
      loadTopics();
    } catch (err) {
      alert('Failed to import syllabus template: ' + err.message);
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleGoToSteps = (topic) => {
    setExpandedTopicId(topic.topicId);
    setActiveTab('topics');
  };

  const handleSaveTopic = async (e) => {
    e.preventDefault();
    if (!topicForm.title) return alert('Please enter topic title');

    try {
      const newWeek = Number(topicForm.order) || 1;
      const targetTerm = topicForm.term || 'Term 1';
      const topicId = topicForm.topicId || editingTopic?.topicId || editingTopic?.id;
      const oldWeek = editingTopic?.order !== undefined && editingTopic?.order !== null ? Number(editingTopic.order) : null;

      const payload = {
        ...(editingTopic || {}),
        topicId,
        title: topicForm.title.trim(),
        term: targetTerm,
        order: newWeek,
        classId: selectedClass,
        subjectId: selectedSubject,
        stepsCount: editingTopic?.stepsCount || editingTopic?.steps?.length || 0,
        steps: editingTopic?.steps || [],
      };

      // Check if another topic in the same term already occupies newWeek
      const termTopics = topics.filter(
        (t) => normalizeTerm(t.term) === normalizeTerm(targetTerm) && (t.topicId || t.id) !== topicId
      );

      const conflictingTopic = termTopics.find((t, idx) => {
        const tWeek = t.order !== undefined && t.order !== null ? Number(t.order) : idx + 1;
        return tWeek === newWeek;
      });

      if (conflictingTopic && oldWeek && oldWeek !== newWeek) {
        // Automatic Swap: the conflicting topic takes the old week!
        const swappedTopicPayload = {
          ...conflictingTopic,
          topicId: conflictingTopic.topicId || conflictingTopic.id,
          order: oldWeek,
          classId: selectedClass,
          subjectId: selectedSubject,
        };
        await saveCurriculumTopic(swappedTopicPayload);
        await saveCurriculumTopic(payload);
        setFeedbackMsg(`✓ Moved "${payload.title}" to Week ${newWeek} and swapped "${conflictingTopic.title}" to Week ${oldWeek}!`);
      } else {
        await saveCurriculumTopic(payload);
        setFeedbackMsg(`✓ Topic "${payload.title}" saved successfully to Week ${newWeek}!`);
      }

      setIsTopicModalOpen(false);
      await loadTopics();
      setTimeout(() => setFeedbackMsg(''), 4000);
    } catch (err) {
      alert('Failed to save topic: ' + err.message);
    }
  };

  const handleDeleteTopic = async (topicOrId) => {
    const isObj = typeof topicOrId === 'object' && topicOrId !== null;
    const title = isObj ? (topicOrId.title || topicOrId.name) : 'this topic';
    if (!window.confirm(`Delete "${title}" and all its lesson steps?`)) return;
    try {
      await deleteCurriculumTopic(topicOrId, {
        classId: selectedClass,
        subjectId: selectedSubject,
      });
      loadTopics();
    } catch (err) {
      alert('Failed to delete topic: ' + err.message);
    }
  };

  const handleBatchDeleteTopics = async (topicsToDelete) => {
    if (!topicsToDelete || topicsToDelete.length === 0) return;
    const count = topicsToDelete.length;
    if (!window.confirm(`Are you sure you want to delete ${count} selected topic${count > 1 ? 's' : ''} and all associated lesson steps?`)) {
      return;
    }

    setIsDeletingBatch(true);
    try {
      for (const topic of topicsToDelete) {
        await deleteCurriculumTopic(topic, {
          classId: selectedClass,
          subjectId: selectedSubject,
        });
      }
      const deletedIds = new Set(topicsToDelete.map((t) => t.topicId || t.id));
      setSelectedTopicIds((prev) => prev.filter((id) => !deletedIds.has(id)));
      await loadTopics();
    } catch (err) {
      alert('Failed to delete selected topics: ' + err.message);
    } finally {
      setIsDeletingBatch(false);
    }
  };

  const toggleSelectTopic = (topicId) => {
    setSelectedTopicIds((prev) =>
      prev.includes(topicId) ? prev.filter((id) => id !== topicId) : [...prev, topicId]
    );
  };

  const toggleSelectAllInTerm = (termTopics) => {
    const termTopicIds = termTopics.map((t) => t.topicId || t.id);
    const allSelected = termTopicIds.length > 0 && termTopicIds.every((id) => selectedTopicIds.includes(id));

    if (allSelected) {
      setSelectedTopicIds((prev) => prev.filter((id) => !termTopicIds.includes(id)));
    } else {
      setSelectedTopicIds((prev) => Array.from(new Set([...prev, ...termTopicIds])));
    }
  };

  // --- STEP HANDLERS ---

  const handleOpenStepModal = (topic, stepIndex = null) => {
    setTargetTopicForStep(topic);
    setEditingStepIndex(stepIndex);

    if (stepIndex !== null && topic.steps && topic.steps[stepIndex]) {
      const s = topic.steps[stepIndex];
      const cq = s.checkQuestion || {};
      const opts = cq.options || ['', '', '', ''];
      setStepForm({
        stepNumber: s.stepNumber || stepIndex + 1,
        title: s.title || '',
        concept: s.concept || '',
        analogy: s.analogy || '',
        keyRule: s.keyRule || '',
        example: s.example || '',
        checkQuestionText: cq.text || '',
        optionA: opts[0] || '',
        optionB: opts[1] || '',
        optionC: opts[2] || '',
        optionD: opts[3] || '',
        correctAnswer: cq.correctAnswer || '',
        explanation: cq.explanation || '',
      });
    } else {
      const nextNum = (topic.steps?.length || 0) + 1;
      setStepForm({
        stepNumber: nextNum,
        title: `Step ${nextNum}`,
        concept: '',
        analogy: '',
        keyRule: '',
        example: '',
        checkQuestionText: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        correctAnswer: '',
        explanation: '',
      });
    }
    setIsStepModalOpen(true);
  };

  const handleSaveStep = async (e) => {
    e.preventDefault();
    if (!targetTopicForStep) return;

    const newStepObj = {
      stepNumber: Number(stepForm.stepNumber),
      title: stepForm.title,
      concept: stepForm.concept,
      analogy: stepForm.analogy,
      keyRule: stepForm.keyRule,
      example: stepForm.example,
      checkQuestion: {
        text: stepForm.checkQuestionText,
        options: [stepForm.optionA, stepForm.optionB, stepForm.optionC, stepForm.optionD].filter(Boolean),
        correctAnswer: stepForm.correctAnswer,
        explanation: stepForm.explanation,
      },
    };

    const currentSteps = [...(targetTopicForStep.steps || [])];
    if (editingStepIndex !== null) {
      currentSteps[editingStepIndex] = newStepObj;
    } else {
      currentSteps.push(newStepObj);
    }

    try {
      await saveCurriculumTopic({
        ...targetTopicForStep,
        steps: currentSteps,
        stepsCount: currentSteps.length,
      });
      setIsStepModalOpen(false);
      loadTopics();
    } catch (err) {
      alert('Failed to save lesson step: ' + err.message);
    }
  };

  const handleDeleteStep = async (topic, stepIndex) => {
    if (!window.confirm('Delete this lesson step?')) return;
    const updatedSteps = (topic.steps || []).filter((_, idx) => idx !== stepIndex);
    try {
      await saveCurriculumTopic({
        ...topic,
        steps: updatedSteps,
        stepsCount: updatedSteps.length,
      });
      loadTopics();
    } catch (err) {
      alert('Failed to delete step: ' + err.message);
    }
  };

  // --- QUESTION HANDLERS ---

  const handleOpenQuestionModal = (q = null) => {
    if (q) {
      setEditingQuestion(q);
      const opts = q.options || ['', '', '', ''];
      setQuestionForm({
        id: q.id,
        topicId: q.topicId || (topics[0]?.topicId || ''),
        question: q.question || '',
        optionA: opts[0] || '',
        optionB: opts[1] || '',
        optionC: opts[2] || '',
        optionD: opts[3] || '',
        correctAnswer: q.correctAnswer || '',
        explanation: q.explanation || '',
        difficulty: q.difficulty || 1,
        approved: q.approved !== false,
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        id: `q_${Date.now()}`,
        topicId: topics[0]?.topicId || '',
        question: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        correctAnswer: '',
        explanation: '',
        difficulty: 1,
        approved: true,
      });
    }
    setIsQuestionModalOpen(true);
  };

  const handleSaveQuestion = async (e) => {
    e.preventDefault();
    if (!questionForm.question) return alert('Question text is required');
    if (!questionForm.correctAnswer) return alert('Correct answer is required');

    const options = [questionForm.optionA, questionForm.optionB, questionForm.optionC, questionForm.optionD].filter(Boolean);

    try {
      await saveCurriculumQuestion({
        id: questionForm.id,
        topicId: questionForm.topicId,
        classId: selectedClass,
        subjectId: selectedSubject,
        question: questionForm.question,
        options,
        correctAnswer: questionForm.correctAnswer,
        explanation: questionForm.explanation,
        difficulty: Number(questionForm.difficulty),
        approved: questionForm.approved,
        status: questionForm.approved ? 'approved' : 'pending',
      });
      setIsQuestionModalOpen(false);
      loadQuestions();
    } catch (err) {
      alert('Failed to save question: ' + err.message);
    }
  };

  const handleToggleApproval = async (qId, currentApproved) => {
    try {
      await setQuestionApproval(qId, !currentApproved);
      loadQuestions();
    } catch (err) {
      alert('Failed to update approval status: ' + err.message);
    }
  };

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await deleteCurriculumQuestion(qId);
      loadQuestions();
    } catch (err) {
      alert('Failed to delete question: ' + err.message);
    }
  };

  const filteredQuestions = questions.filter((q) => {
    if (questionFilter === 'approved') return q.approved === true || q.status === 'approved';
    if (questionFilter === 'pending') return q.approved === false || q.status === 'pending';
    return true;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-poppins whitespace-nowrap">
            Curriculum Management
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => setShowClassOverview(!showClassOverview)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold shadow-sm transition-all ${
              showClassOverview
                ? 'bg-indigo-800 text-white ring-2 ring-indigo-300'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            Subjects
          </button>


          <button
            onClick={handleQuickSeed}
            disabled={seedingLoading}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all disabled:opacity-50"
          >
            {seedingLoading
              ? 'Generating...'
              : `Auto-Generate ${CLASSES.find((c) => c.id === selectedClass)?.name || selectedClass.toUpperCase()} ${SUBJECTS.find((s) => s.id === selectedSubject)?.name || selectedSubject}`}
          </button>
        </div>
      </div>

      {/* Floating / Sticky Feedback Toast Banner */}
      {feedbackMsg && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50 max-w-lg w-full px-4 pointer-events-auto">
          <div
            style={{ backgroundColor: '#059669', color: '#ffffff' }}
            className="p-4 text-white rounded-2xl text-sm font-bold flex items-center justify-between gap-3 shadow-2xl border border-emerald-400"
          >
            <span className="flex items-center gap-2.5">
              <span
                style={{ backgroundColor: '#047857' }}
                className="text-base font-black w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              >
                ✓
              </span>
              <span className="text-white leading-snug">{feedbackMsg}</span>
            </span>
            <button
              onClick={() => setFeedbackMsg('')}
              className="text-white/80 hover:text-white font-bold ml-3 text-lg leading-none cursor-pointer flex-shrink-0"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {feedbackMsg && (
        <div
          style={{ backgroundColor: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}
          className="p-4 border rounded-xl text-sm font-semibold flex items-center justify-between shadow-2xs"
        >
          <span className="flex items-center gap-2">
            <span style={{ color: '#059669' }} className="font-bold text-base">✓</span>
            <span>{feedbackMsg}</span>
          </span>
          <button
            onClick={() => setFeedbackMsg('')}
            style={{ color: '#059669' }}
            className="font-bold ml-4 hover:opacity-80"
          >
            ✕
          </button>
        </div>
      )}

      {/* Class Syllabus Matrix (Accordion / Overview) */}
      {showClassOverview && (
        <div className="bg-white p-5 rounded-2xl border border-indigo-200 shadow-sm space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MdLayers className="text-xl text-indigo-600" />
              <h3 className="text-sm font-bold text-gray-900 font-poppins">
                {CLASSES.find((c) => c.id === selectedClass)?.name} Syllabus Matrix ({availableSubjectsForClass.length} Subjects)
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 hidden md:inline">Click any subject to switch active syllabus view</span>
              <button
                type="button"
                onClick={() => {
                  setNewSubjectForm({
                    name: '',
                    id: '',
                    idManual: false,
                    stream: isJuniorClass ? 'general' : (selectedStreamFilter !== 'all' ? selectedStreamFilter : 'science'),
                    classes: [selectedClass],
                  });
                  setIsAddSubjectModalOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all active:scale-95 cursor-pointer"
                title={`Add subject to ${CLASSES.find((c) => c.id === selectedClass)?.name}`}
              >
                <MdAdd className="text-base" />
                <span>+ Add Subject</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {availableSubjectsForClass.map((s) => {
              const isCurrent = s.id === selectedSubject;
              return (
                <div
                  key={s.id}
                  onClick={() => setSelectedSubject(s.id)}
                  className={`group relative p-3 rounded-xl border text-left transition-all flex flex-col justify-between cursor-pointer ${isCurrent
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm font-bold'
                    : 'bg-gray-50 hover:bg-indigo-50 border-gray-200 text-gray-800 hover:border-indigo-300'
                    }`}
                >
                  <div className="flex items-start justify-between gap-1 w-full">
                    <span className="text-xs truncate block font-bold leading-tight flex-1">{s.name}</span>
                    {/* Quick Edit & Delete Actions directly on tile */}
                    <div
                      className={`flex items-center gap-0.5 transition-opacity ${
                        isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => handleOpenEditSubjectModal(s)}
                        className={`p-1 rounded transition-colors ${
                          isCurrent
                            ? 'text-indigo-200 hover:text-white hover:bg-indigo-700'
                            : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-100'
                        }`}
                        title={`Edit ${s.name}`}
                      >
                        <MdEdit className="text-xs" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSubject(s)}
                        className={`p-1 rounded transition-colors ${
                          isCurrent
                            ? 'text-indigo-200 hover:text-red-300 hover:bg-indigo-700'
                            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                        }`}
                        title={`Delete ${s.name}`}
                      >
                        <MdDelete className="text-xs" />
                      </button>
                    </div>
                  </div>
                  <span className={`text-[10px] mt-2 flex items-center justify-between ${isCurrent ? 'text-indigo-200' : 'text-gray-400'}`}>
                    <span>{s.stream ? s.stream.toUpperCase() : 'CORE'}</span>
                    {s.isCustom && <span className="text-[9px] px-1 bg-indigo-500 text-white rounded font-bold">CUSTOM</span>}
                  </span>
                </div>
              );
            })}

            {/* + Add Subject Card Right in the Matrix Grid (where circled by user in red!) */}
            <button
              type="button"
              onClick={() => {
                setNewSubjectForm({
                  name: '',
                  id: '',
                  idManual: false,
                  stream: isJuniorClass ? 'general' : (selectedStreamFilter !== 'all' ? selectedStreamFilter : 'science'),
                  classes: [selectedClass],
                });
                setIsAddSubjectModalOpen(true);
              }}
              className="p-3 rounded-xl border-2 border-dashed border-indigo-400 hover:border-indigo-700 bg-indigo-50/70 hover:bg-indigo-100 text-indigo-700 transition-all flex flex-col justify-center items-center text-center shadow-sm hover:shadow active:scale-95 group cursor-pointer min-h-[72px]"
              title={`Add New Subject to ${CLASSES.find((c) => c.id === selectedClass)?.name || selectedClass.toUpperCase()}`}
            >
              <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-700 group-hover:text-indigo-950">
                <div className="w-5 h-5 rounded-full bg-indigo-600 group-hover:bg-indigo-700 text-white flex items-center justify-center transition-colors shadow-xs">
                  <MdAdd className="text-sm font-bold" />
                </div>
                <span>+ Add Subject</span>
              </div>
              <span className="text-[10px] text-indigo-600 font-semibold mt-1">
                for {CLASSES.find((c) => c.id === selectedClass)?.name || selectedClass.toUpperCase()}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Class & Subject Selector Bar */}
      <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
          {/* Class Select */}
          <div>
            <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Target Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-800 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500"
            >
              {CLASSES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Stream Filter - ONLY FOR SENIOR CLASSES (SS1, SS2, SS3, UTME). For Junior classes (JSS1-3), hidden because Junior is General only! */}
          {!isJuniorClass ? (
            <div>
              <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Stream Filter</label>
              <select
                value={selectedStreamFilter}
                onChange={(e) => setSelectedStreamFilter(e.target.value)}
                className="px-3.5 py-2 border border-indigo-200 rounded-xl text-sm font-semibold text-indigo-900 bg-indigo-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Streams</option>
                <option value="science">🔬 Science</option>
                <option value="commercial">📊 Commercial</option>
                <option value="art">🎨 Arts & Humanities</option>
                <option value="general">📚 Core / General</option>
              </select>
            </div>
          ) : (
            <div className="self-end pb-1.5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-800 text-xs font-semibold rounded-xl border border-amber-200 shadow-sm">
                <span>📘</span> Junior General (No Streams)
              </span>
            </div>
          )}

          {/* Subject Select */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-bold uppercase text-gray-500">Subject</label>
              <span className="text-[11px] text-gray-400 font-medium ml-2">({availableSubjectsForClass.length} available)</span>
            </div>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-800 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500"
            >
              {availableSubjectsForClass.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.isCustom ? '★ (Custom)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons: Add, Edit Current, Delete Current */}
          <div className="flex items-end gap-2 self-end">
            <button
              type="button"
              onClick={() => {
                setEditingSubject(null);
                setNewSubjectForm({
                  name: '',
                  id: '',
                  idManual: false,
                  stream: isJuniorClass ? 'general' : (selectedStreamFilter !== 'all' ? selectedStreamFilter : 'science'),
                  classes: [selectedClass],
                });
                setIsAddSubjectModalOpen(true);
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-sm transition-all hover:shadow active:scale-95 cursor-pointer"
              title="Add a custom subject to curriculum"
            >
              <MdAdd className="text-lg" />
              <span>Add Subject</span>
            </button>

            {/* Edit Current Selected Subject */}
            <button
              type="button"
              onClick={() => {
                const cur = allSubjects.find((s) => s.id === selectedSubject);
                if (cur) handleOpenEditSubjectModal(cur);
              }}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 text-gray-700 hover:text-indigo-700 rounded-xl text-sm font-semibold transition-all shadow-xs"
              title="Edit Currently Selected Subject"
            >
              <MdEdit className="text-base" />
              <span className="hidden sm:inline">Edit</span>
            </button>

            {/* Delete Current Selected Subject */}
            <button
              type="button"
              onClick={() => {
                const cur = allSubjects.find((s) => s.id === selectedSubject);
                if (cur) handleDeleteSubject(cur);
              }}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 hover:border-red-400 hover:bg-red-50 text-gray-700 hover:text-red-700 rounded-xl text-sm font-semibold transition-all shadow-xs"
              title="Delete Currently Selected Subject"
            >
              <MdDelete className="text-base" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          </div>
        </div>

        {/* 3-Tab Switcher */}
        <div className="flex p-1 bg-gray-100 rounded-xl w-full md:w-auto flex-wrap gap-1">
          <button
            onClick={() => setActiveTab('syllabus')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'syllabus' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            <MdMenuBook className="text-base" />
            Syllabus Scheme ({topics.length})
          </button>
          <button
            onClick={() => setActiveTab('topics')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'topics' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            <MdSchool className="text-base" />
            Lesson Steps
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'questions' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            <MdQuiz className="text-base" />
            Question Bank ({questions.length})
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* TAB 1: SYLLABUS / SCHEME OF WORK (TERM-BY-TERM OUTLINE)        */}
      {/* ------------------------------------------------------------- */}
      {activeTab === 'syllabus' && (
        <div className="space-y-6">
          {/* Top Term Summary Card */}
          <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-purple-900 rounded-2xl p-6 text-white shadow-md flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold font-poppins">
                {CLASSES.find((c) => c.id === selectedClass)?.name} &bull; {SUBJECTS.find((s) => s.id === selectedSubject)?.name}
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleOpenTopicModal(null, 'Term 1')}
                className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-900 hover:bg-indigo-50 rounded-xl text-sm font-bold shadow-sm transition-all"
              >
                <MdAdd className="text-lg" />
                Add Topic
              </button>
              <button
                onClick={() => setIsDocImporterOpen(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold shadow-sm transition-all cursor-pointer"
                title="Import curriculum scheme & questions from document"
              >
                Import File
              </button>
            </div>
          </div>

          {/* Terms Overview & Expand/Collapse All Toolbar */}
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Terms & Schemes ({topics.length} total topics)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExpandAllTerms}
                className="text-xs font-bold text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors shadow-2xs cursor-pointer"
              >
                Expand All
              </button>
              <button
                type="button"
                onClick={handleCollapseAllTerms}
                className="text-xs font-bold text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors shadow-2xs cursor-pointer"
              >
                Collapse All
              </button>
            </div>
          </div>

          {/* Three Academic Terms */}
          <div className="grid grid-cols-1 gap-6">
            {['Term 1', 'Term 2', 'Term 3'].map((termName, termIdx) => {
              const termTopics = syllabusByTerm[termName] || [];
              const isExpanded = !!expandedTerms[termName];
              const termColor =
                termIdx === 0
                  ? { bg: 'bg-blue-50/70', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-600' }
                  : termIdx === 1
                    ? { bg: 'bg-green-50/70', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-600' }
                    : { bg: 'bg-purple-50/70', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-600' };

              return (
                <div
                  key={termName}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all"
                >
                  {/* Term Header (Clickable to Toggle) */}
                  <div
                    onClick={() => toggleTerm(termName)}
                    className={`p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${termColor.bg} ${isExpanded ? `border-b ${termColor.border}` : ''
                      } cursor-pointer select-none transition-colors hover:bg-opacity-90`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl ${termColor.badge} text-white flex items-center justify-center font-bold text-sm shadow-sm flex-shrink-0`}>
                        T{termIdx + 1}
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 font-poppins">
                          {termName === 'Term 1' ? '1st Term' : termName === 'Term 2' ? '2nd Term' : '3rd Term'} Syllabus & Scheme
                          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-white text-gray-700 border border-gray-200">
                            {termTopics.length} {termTopics.length === 1 ? 'Topic' : 'Topics'}
                          </span>
                        </h3>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {termTopics.length > 0
                            ? `Weeks 1 to ${termTopics.length} scheme of work scheduled for this term`
                            : 'No topics scheduled for this term yet'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      {termTopics.length === 0 && (
                        <button
                          onClick={() => handleImportSyllabusTemplate(termName)}
                          className="px-3.5 py-1.5 bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50 rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                        >
                          Import NERDC Template
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenTopicModal(null, termName)}
                        className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
                      >
                        <MdAdd className="text-base" /> Add Topic
                      </button>

                      {/* Expand / Collapse Toggle Button */}
                      <button
                        type="button"
                        onClick={() => toggleTerm(termName)}
                        className={`px-3.5 py-1.5 rounded-lg text-xs font-bold border transition-all cursor-pointer ${isExpanded
                          ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100 shadow-xs'
                          : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50 shadow-xs'
                          }`}
                        title={isExpanded ? 'Hide topics list' : 'View all topics'}
                      >
                        {isExpanded ? 'Hide' : `View (${termTopics.length})`}
                      </button>
                    </div>
                  </div>

                  {/* Term Topics List (Only visible when expanded) */}
                  {isExpanded && (
                    termTopics.length === 0 ? (
                      <div className="p-8 text-center text-gray-500 space-y-2">
                        <p className="text-sm font-semibold text-gray-700">No syllabus topics added for {termName} yet.</p>
                        <p className="text-xs text-gray-400">
                          Click "+ Add Topic" to add a custom topic or "Import NERDC Template" to populate standard topics.
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {termTopics.map((topic, index) => {
                          const qCount = questionsCountByTopic[topic.topicId] || 0;
                          const stepCount = topic.steps?.length || topic.stepsCount || 0;
                          const weekNumber = topic.order || index + 1;

                          return (
                            <div
                              key={topic.topicId}
                              className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50/80 transition-colors"
                            >
                              <div className="flex items-start sm:items-center gap-3.5 flex-1 min-w-0">
                                <span className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs flex-shrink-0 mt-0.5 sm:mt-0 shadow-2xs">
                                  W{weekNumber}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2.5">
                                    <h4 className="text-sm font-bold text-gray-900 truncate">
                                      {topic.title}
                                    </h4>

                                    {/* Static Scheduled Week Badge (Read-only, change via Edit modal) */}
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50/80 text-indigo-700 border border-indigo-100/90 shadow-2xs">
                                      Week {weekNumber}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 font-medium">
                                    <span>
                                      {stepCount} {stepCount === 1 ? 'lesson step' : 'lesson steps'}
                                    </span>
                                    <span>&bull;</span>
                                    <span>
                                      {qCount} {qCount === 1 ? 'WAEC question' : 'WAEC questions'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                                <button
                                  onClick={() => handleGoToSteps(topic)}
                                  title="Configure lesson concepts, analogies and worked examples"
                                  className="px-3.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200/80 rounded-lg text-xs font-bold transition-all cursor-pointer shadow-2xs"
                                >
                                  Configure Steps
                                </button>
                                <button
                                  onClick={() => handleOpenTopicModal(topic, termName, weekNumber)}
                                  title="Edit topic details"
                                  className="px-3 py-1.5 text-xs font-bold text-gray-700 hover:text-indigo-700 bg-gray-100 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-200 rounded-lg transition-all cursor-pointer shadow-2xs"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteTopic(topic)}
                                  title="Delete this topic"
                                  className="px-3 py-1.5 text-xs font-bold text-red-600 hover:text-red-700 bg-red-50/70 hover:bg-red-100 border border-red-200 rounded-lg transition-all cursor-pointer shadow-2xs"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* TAB 2: TOPICS & LESSON STEPS (DETAILED STEP BUILDER)           */}
      {/* ------------------------------------------------------------- */}
      {activeTab === 'topics' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">
              Curriculum Topics ({selectedClass.toUpperCase()} - {SUBJECTS.find((s) => s.id === selectedSubject)?.name})
            </h2>
            <button
              onClick={() => handleOpenTopicModal()}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all"
            >
              <MdAdd className="text-lg" />
              Add Topic
            </button>
          </div>

          {loadingTopics ? (
            <div className="p-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
              Loading curriculum topics...
            </div>
          ) : topics.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-200">
              <MdMenuBook className="text-4xl text-gray-300 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-700">No topics added for this subject yet.</p>
              <p className="text-sm text-gray-500 mt-1">
                Click "Add Topic" or use the "Seed SS 1 Math" button to import default content.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {topics.map((topic) => {
                const isExpanded = expandedTopicId === topic.topicId;
                const steps = topic.steps || [];

                return (
                  <div
                    key={topic.topicId}
                    className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden transition-all"
                  >
                    {/* Topic Header Row */}
                    <div className="p-5 flex items-center justify-between bg-gray-50/50 hover:bg-gray-50">
                      <div
                        onClick={() => setExpandedTopicId(isExpanded ? null : topic.topicId)}
                        className="flex items-center gap-3 cursor-pointer flex-1"
                      >
                        <span className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm">
                          {topic.order || 1}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-bold text-gray-900">{topic.title}</h3>
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded font-medium">
                              {topic.term || 'Term 1'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {steps.length} lesson steps configured
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenStepModal(topic)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold"
                        >
                          <MdAdd /> Add Step
                        </button>
                        <button
                          onClick={() => handleOpenTopicModal(topic, topic.term, topic.order)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        >
                          <MdEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteTopic(topic)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        >
                          <MdDelete />
                        </button>
                        <button
                          onClick={() => setExpandedTopicId(isExpanded ? null : topic.topicId)}
                          className="p-2 text-gray-400 hover:text-gray-700"
                        >
                          {isExpanded ? <MdExpandLess className="text-xl" /> : <MdExpandMore className="text-xl" />}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Steps Accordion Content */}
                    {isExpanded && (
                      <div className="p-5 border-t border-gray-100 space-y-4">
                        {steps.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">No steps added yet. Click "+ Add Step" above.</p>
                        ) : (
                          steps.map((step, idx) => (
                            <div
                              key={idx}
                              className="p-4 rounded-xl border border-gray-200 bg-white hover:border-indigo-200 transition-all space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-sm text-indigo-900">
                                  Step {step.stepNumber || idx + 1}: {step.title}
                                </span>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleOpenStepModal(topic, idx)}
                                    className="p-1.5 text-gray-500 hover:text-indigo-600 rounded"
                                  >
                                    <MdEdit className="text-base" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteStep(topic, idx)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                                  >
                                    <MdDelete className="text-base" />
                                  </button>
                                </div>
                              </div>

                              <p className="text-xs text-gray-700">
                                <strong className="text-gray-900">Concept:</strong> {step.concept}
                              </p>
                              {step.analogy && (
                                <p className="text-xs text-amber-800 bg-amber-50 p-2 rounded-lg">
                                  <strong>Everyday Analogy:</strong> {step.analogy}
                                </p>
                              )}
                              {step.keyRule && (
                                <p className="text-xs text-indigo-800 bg-indigo-50 p-2 rounded-lg">
                                  <strong>Key Rule:</strong> {step.keyRule}
                                </p>
                              )}
                              {step.checkQuestion?.text && (
                                <div className="text-xs bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                  <strong>Check Question:</strong> {step.checkQuestion.text}
                                  <div className="mt-1 text-emerald-700 font-semibold">
                                    Correct Answer: {step.checkQuestion.correctAnswer}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* TAB 2: QUESTION BANK & APPROVALS */}
      {/* ------------------------------------------------------------- */}
      {activeTab === 'questions' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500 uppercase">Filter:</span>
              <button
                onClick={() => setQuestionFilter('all')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${questionFilter === 'all'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                All ({questions.length})
              </button>
              <button
                onClick={() => setQuestionFilter('approved')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${questionFilter === 'approved'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Approved ({questions.filter((q) => q.approved !== false && q.status !== 'pending').length})
              </button>
              <button
                onClick={() => setQuestionFilter('pending')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${questionFilter === 'pending'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Pending Approval ({questions.filter((q) => q.approved === false || q.status === 'pending').length})
              </button>
            </div>

            <button
              onClick={() => handleOpenQuestionModal()}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all"
            >
              <MdAdd className="text-lg" />
              Add Question
            </button>
          </div>

          {loadingQuestions ? (
            <div className="p-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
              Loading questions...
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-2xl border border-gray-200">
              <MdQuiz className="text-4xl text-gray-300 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-700">No questions found matching criteria.</p>
              <p className="text-sm text-gray-500 mt-1">Click "Add Question" above to insert WAEC questions.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Mobile Horizontal Scroll Indicator Banner */}
              <div className="md:hidden px-4 py-2 bg-indigo-50/80 border-b border-indigo-100 flex items-center justify-between text-xs text-indigo-700 font-medium">
                <span>Swipe horizontally to view all columns</span>
                <span className="font-bold">⇄</span>
              </div>

              {/* Horizontal Scroll Container for Mobile */}
              <div className="overflow-x-auto w-full scrollbar-thin">
                <table className="w-full min-w-[880px] text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 font-semibold text-xs uppercase">
                      <th className="px-5 py-3.5 min-w-[360px]">Question</th>
                      <th className="px-5 py-3.5 min-w-[160px]">Topic</th>
                      <th className="px-5 py-3.5 min-w-[140px]">Correct Answer</th>
                      <th className="px-5 py-3.5 min-w-[100px]">Difficulty</th>
                      <th className="px-5 py-3.5 min-w-[120px]">Status</th>
                      <th className="px-5 py-3.5 min-w-[90px] text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredQuestions.map((q, index) => {
                      const isApproved = q.approved !== false && q.status !== 'pending';

                      // Resolve clean, human-readable topic name (avoiding raw alphanumeric database hashes)
                      const isRawHash = (str) => typeof str === 'string' && /^[a-zA-Z0-9_-]{16,}$/.test(str);
                      const matchedTopic = topics.find(
                        (t) => (t.topicId && t.topicId === q.topicId) || (t.id && t.id === q.topicId)
                      );
                      let displayTopic = matchedTopic?.title;
                      if (!displayTopic && q.topicTitle && !isRawHash(q.topicTitle)) {
                        displayTopic = q.topicTitle;
                      }
                      if (!displayTopic && q.source && !isRawHash(q.source) && q.source !== 'Teacher Quiz Builder') {
                        displayTopic = q.source;
                      }
                      if (!displayTopic && q.topicId && !isRawHash(q.topicId)) {
                        displayTopic = q.topicId.replace(/^[a-z0-9]+_[a-z0-9]+_/, '').replace(/_/g, ' ');
                      }
                      if (!displayTopic) {
                        const contextMatch = topics.find((t) =>
                          t.title &&
                          (q.question?.toLowerCase().includes(t.title.toLowerCase()) ||
                            q.options?.some((o) => typeof o === 'string' && o.toLowerCase().includes(t.title.toLowerCase())))
                        );
                        if (contextMatch?.title) {
                          displayTopic = contextMatch.title;
                        } else {
                          const curSubj = SUBJECTS.find((s) => s.id === selectedSubject);
                          displayTopic = curSubj ? `${curSubj.name} Quiz` : 'General Practice';
                        }
                      }

                      return (
                        <tr key={q.id} className="hover:bg-gray-50/80 transition-colors">
                          <td className="px-5 py-4 font-medium text-gray-900 max-w-lg">
                            <div className="flex items-start gap-2.5">
                              {/* Question Number 1, 2, 3, etc. */}
                              <span className="flex-shrink-0 font-bold text-sm text-indigo-600 select-none pt-0.5">
                                {index + 1}.
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 leading-snug">{q.question}</div>
                                {/* Distinct, Lettered Multiple-Choice Options */}
                                {Array.isArray(q.options) && q.options.length > 0 && (
                                  <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                                    {q.options.map((opt, i) => {
                                      const letter = String.fromCharCode(65 + i);
                                      const isCorrect =
                                        String(opt).trim().toLowerCase() === String(q.correctAnswer).trim().toLowerCase();
                                      return (
                                        <span
                                          key={i}
                                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs transition-colors border ${isCorrect
                                            ? 'bg-emerald-50 text-emerald-900 border-emerald-300 font-bold shadow-2xs'
                                            : 'bg-gray-50 text-gray-700 border-gray-200 font-medium'
                                            }`}
                                        >
                                          <span
                                            className={`font-bold mr-1.5 ${isCorrect ? 'text-emerald-700' : 'text-gray-400'
                                              }`}
                                          >
                                            {isCorrect ? '✓ ' : ''}{letter}.
                                          </span>
                                          <span>{opt}</span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 max-w-[200px] truncate"
                              title={displayTopic}
                            >
                              {displayTopic}
                            </span>
                          </td>
                          <td className="px-5 py-4 font-bold text-emerald-700">{q.correctAnswer}</td>
                          <td className="px-5 py-3.5">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {q.difficulty === 3 ? 'Hard' : q.difficulty === 2 ? 'Medium' : 'Standard'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <button
                              onClick={() => handleToggleApproval(q.id, isApproved)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${isApproved ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                                }`}
                            >
                              {isApproved ? <MdCheckCircle /> : <MdCancel />}
                              {isApproved ? 'Approved' : 'Pending'}
                            </button>
                          </td>
                          <td className="px-5 py-3.5 text-right space-x-2">
                            <button
                              onClick={() => handleOpenQuestionModal(q)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 rounded"
                            >
                              <MdEdit className="text-base" />
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(q.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            >
                              <MdDelete className="text-base" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL 1: ADD/EDIT TOPIC */}
      {/* ------------------------------------------------------------- */}
      {isTopicModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">
              {editingTopic ? 'Edit Topic' : 'Add New Curriculum Topic'}
            </h3>
            <form onSubmit={handleSaveTopic} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Topic Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Indices and Logarithms"
                  value={topicForm.title}
                  onChange={(e) => setTopicForm({ ...topicForm, title: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Academic Term</label>
                  <select
                    value={topicForm.term}
                    onChange={(e) => setTopicForm({ ...topicForm, term: e.target.value })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm"
                  >
                    <option value="Term 1">Term 1</option>
                    <option value="Term 2">Term 2</option>
                    <option value="Term 3">Term 3</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Scheduled Week</label>
                  <select
                    value={topicForm.order || 1}
                    onChange={(e) => setTopicForm({ ...topicForm, order: Number(e.target.value) })}
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none cursor-pointer"
                  >
                    {Array.from({ length: 13 }, (_, i) => i + 1).map((w) => (
                      <option key={w} value={w}>
                        Week {w}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsTopicModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold"
                >
                  Save Topic
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL 2: ADD/EDIT LESSON STEP */}
      {/* ------------------------------------------------------------- */}
      {isStepModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 space-y-4 shadow-xl my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900">
              {editingStepIndex !== null ? 'Edit Lesson Step' : 'Add Lesson Step'} - {targetTopicForStep?.title}
            </h3>
            <form onSubmit={handleSaveStep} className="space-y-3.5">
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Step #</label>
                  <input
                    type="number"
                    value={stepForm.stepNumber}
                    onChange={(e) => setStepForm({ ...stepForm, stepNumber: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Step Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Introduction to Binary Numbers"
                    value={stepForm.title}
                    onChange={(e) => setStepForm({ ...stepForm, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Core Concept Explanation</label>
                <textarea
                  rows="2"
                  required
                  placeholder="Clear conceptual explanation..."
                  value={stepForm.concept}
                  onChange={(e) => setStepForm({ ...stepForm, concept: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-amber-800 uppercase mb-1">Everyday Nigerian Analogy</label>
                <textarea
                  rows="2"
                  placeholder="Relate to daily life (e.g. market items, food, egg crates, bus fares)..."
                  value={stepForm.analogy}
                  onChange={(e) => setStepForm({ ...stepForm, analogy: e.target.value })}
                  className="w-full px-3 py-2 border border-amber-300 bg-amber-50/50 rounded-xl text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-indigo-800 uppercase mb-1">Golden Key Rule</label>
                  <input
                    type="text"
                    placeholder="e.g. Highest digit in base n is n - 1"
                    value={stepForm.keyRule}
                    onChange={(e) => setStepForm({ ...stepForm, keyRule: e.target.value })}
                    className="w-full px-3 py-2 border border-indigo-300 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Worked Example</label>
                  <input
                    type="text"
                    placeholder="e.g. 1101 (base 2) = 13 (base 10)"
                    value={stepForm.example}
                    onChange={(e) => setStepForm({ ...stepForm, example: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-sm"
                  />
                </div>
              </div>

              {/* Check Question Sub-Section */}
              <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 space-y-2.5">
                <span className="text-xs font-bold text-gray-800 uppercase">Interactive Check Question</span>
                <input
                  type="text"
                  placeholder="Question text..."
                  value={stepForm.checkQuestionText}
                  onChange={(e) => setStepForm({ ...stepForm, checkQuestionText: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm bg-white"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Option A"
                    value={stepForm.optionA}
                    onChange={(e) => setStepForm({ ...stepForm, optionA: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-xs bg-white"
                  />
                  <input
                    type="text"
                    placeholder="Option B"
                    value={stepForm.optionB}
                    onChange={(e) => setStepForm({ ...stepForm, optionB: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-xs bg-white"
                  />
                  <input
                    type="text"
                    placeholder="Option C"
                    value={stepForm.optionC}
                    onChange={(e) => setStepForm({ ...stepForm, optionC: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-xs bg-white"
                  />
                  <input
                    type="text"
                    placeholder="Option D"
                    value={stepForm.optionD}
                    onChange={(e) => setStepForm({ ...stepForm, optionD: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-xs bg-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Exact Correct Answer"
                    value={stepForm.correctAnswer}
                    onChange={(e) => setStepForm({ ...stepForm, correctAnswer: e.target.value })}
                    className="px-3 py-1.5 border border-emerald-300 rounded-lg text-xs font-bold bg-white"
                  />
                  <input
                    type="text"
                    placeholder="Explanation for students"
                    value={stepForm.explanation}
                    onChange={(e) => setStepForm({ ...stepForm, explanation: e.target.value })}
                    className="px-3 py-1.5 border rounded-lg text-xs bg-white"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsStepModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold"
                >
                  Save Step
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL 3: ADD/EDIT QUESTION */}
      {/* ------------------------------------------------------------- */}
      {isQuestionModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900">
              {editingQuestion ? 'Edit Question' : 'Add WAEC Practice Question'}
            </h3>
            <form onSubmit={handleSaveQuestion} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Curriculum Topic</label>
                <select
                  value={questionForm.topicId}
                  onChange={(e) => setQuestionForm({ ...questionForm, topicId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                >
                  {topics.map((t) => (
                    <option key={t.topicId} value={t.topicId}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Question Text</label>
                <textarea
                  rows="2"
                  required
                  placeholder="Enter WAEC format question..."
                  value={questionForm.question}
                  onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-0.5">Option A</label>
                  <input
                    type="text"
                    required
                    value={questionForm.optionA}
                    onChange={(e) => setQuestionForm({ ...questionForm, optionA: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-0.5">Option B</label>
                  <input
                    type="text"
                    required
                    value={questionForm.optionB}
                    onChange={(e) => setQuestionForm({ ...questionForm, optionB: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-0.5">Option C</label>
                  <input
                    type="text"
                    required
                    value={questionForm.optionC}
                    onChange={(e) => setQuestionForm({ ...questionForm, optionC: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-0.5">Option D</label>
                  <input
                    type="text"
                    required
                    value={questionForm.optionD}
                    onChange={(e) => setQuestionForm({ ...questionForm, optionD: e.target.value })}
                    className="w-full px-3 py-1.5 border rounded-lg text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">Exact Correct Answer</label>
                  <input
                    type="text"
                    required
                    placeholder="Must match one option"
                    value={questionForm.correctAnswer}
                    onChange={(e) => setQuestionForm({ ...questionForm, correctAnswer: e.target.value })}
                    className="w-full px-3 py-2 border border-emerald-300 rounded-xl text-xs font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Difficulty</label>
                  <select
                    value={questionForm.difficulty}
                    onChange={(e) => setQuestionForm({ ...questionForm, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border rounded-xl text-xs"
                  >
                    <option value={1}>Standard / WAEC Foundation</option>
                    <option value={2}>Intermediate / WAEC Core</option>
                    <option value={3}>Advanced / JAMB Challenge</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Step-by-Step Explanation</label>
                <textarea
                  rows="2"
                  placeholder="Detailed explanation for student review..."
                  value={questionForm.explanation}
                  onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl text-sm"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="approveQ"
                  checked={questionForm.approved}
                  onChange={(e) => setQuestionForm({ ...questionForm, approved: e.target.checked })}
                  className="rounded text-indigo-600"
                />
                <label htmlFor="approveQ" className="text-xs font-semibold text-gray-700">
                  Approved for Student Practice Quizzes
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsQuestionModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold"
                >
                  Save Question
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Custom Subject Modal */}
      {isAddSubjectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
                  {editingSubject ? <MdEdit className="text-xl" /> : <MdMenuBook className="text-xl" />}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingSubject ? `Edit Subject: ${editingSubject.name}` : 'Add Curriculum Subject'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {editingSubject
                      ? 'Update subject name, stream category, or class assignments'
                      : 'Configure subject stream, target classes, and syllabus access'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsAddSubjectModalOpen(false);
                  setEditingSubject(null);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <MdCancel className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSaveNewSubject} className="mt-5 space-y-4">
              {/* Subject Name */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Subject Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Mathematics, Chemistry, Physics, Biology, etc."
                  value={newSubjectForm.name}
                  onChange={(e) => {
                    const val = e.target.value;
                    const autoId = val
                      .toLowerCase()
                      .replace(/[^a-z0-9]/g, '_')
                      .replace(/_+/g, '_')
                      .replace(/^_|_$/g, '');
                    setNewSubjectForm((prev) => ({
                      ...prev,
                      name: val,
                      id: autoId,
                    }));
                  }}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>

              {/* Stream Category */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                  Curriculum Stream
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'general', label: 'General', desc: 'For JSS students only (Junior secondary)', icon: '📚' },
                    { id: 'science', label: 'Science', desc: 'Physics, Chem, Biology, Tech', icon: '🔬' },
                    { id: 'commercial', label: 'Commercial', desc: 'Business, Accounts, Commerce', icon: '📊' },
                    { id: 'art', label: 'Art & Humanities', desc: 'Govt, Lit, CRS, History', icon: '🎨' },
                  ].map((st) => (
                    <label
                      key={st.id}
                      className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
                        newSubjectForm.stream === st.id
                          ? 'border-indigo-600 bg-indigo-50/70 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="subjectStream"
                        value={st.id}
                        checked={newSubjectForm.stream === st.id}
                        onChange={() => {
                          setNewSubjectForm((prev) => {
                            let targetClasses = prev.classes;
                            if (st.id === 'general') {
                              const hasJunior = targetClasses.some((c) => c.startsWith('jss'));
                              if (!hasJunior) {
                                targetClasses = ['jss1', 'jss2', 'jss3'];
                              }
                            } else {
                              const hasSenior = targetClasses.some((c) => c.startsWith('ss') || c === 'utme');
                              if (!hasSenior) {
                                targetClasses = ['ss1', 'ss2', 'ss3'];
                              }
                            }
                            return { ...prev, stream: st.id, classes: targetClasses };
                          });
                        }}
                        className="mt-1 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-800 flex items-center gap-1">
                          <span>{st.icon}</span> {st.label}
                        </div>
                        <div className="text-[10px] text-gray-500 leading-tight">{st.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Target Classes */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase">
                    Target Classes <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setNewSubjectForm((prev) => ({ ...prev, classes: ['ss1', 'ss2', 'ss3'] }))}
                      className="text-[11px] font-semibold text-indigo-600 hover:underline"
                    >
                      Senior (SS1-3)
                    </button>
                    <span className="text-gray-300">&bull;</span>
                    <button
                      type="button"
                      onClick={() => setNewSubjectForm((prev) => ({ ...prev, classes: ['jss1', 'jss2', 'jss3'] }))}
                      className="text-[11px] font-semibold text-indigo-600 hover:underline"
                    >
                      Junior (JSS1-3)
                    </button>
                    <span className="text-gray-300">&bull;</span>
                    <button
                      type="button"
                      onClick={() => setNewSubjectForm((prev) => ({ ...prev, classes: CLASSES.map((c) => c.id) }))}
                      className="text-[11px] font-semibold text-indigo-600 hover:underline"
                    >
                      All Classes
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CLASSES.map((c) => {
                    const isChecked = newSubjectForm.classes.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 p-2 rounded-xl border cursor-pointer text-xs font-semibold transition-all ${
                          isChecked
                            ? 'border-indigo-600 bg-indigo-50 text-indigo-900 font-bold'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700 bg-gray-50/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...newSubjectForm.classes, c.id]
                              : newSubjectForm.classes.filter((cid) => cid !== c.id);
                            setNewSubjectForm((prev) => ({ ...prev, classes: next }));
                          }}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddSubjectModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingSubject}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow transition-all disabled:opacity-50 cursor-pointer"
                >
                  {editingSubject ? <MdEdit className="text-lg" /> : <MdAdd className="text-lg" />}
                  {isSavingSubject ? 'Saving...' : editingSubject ? 'Update Subject' : 'Create Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Document & Curriculum Importer Modal */}
      <AIDocumentImporterModal
        isOpen={isDocImporterOpen}
        onClose={() => setIsDocImporterOpen(false)}
        initialClass={selectedClass}
        initialSubject={selectedSubject}
        initialTerm="Term 1"
        userProfile={userProfile}
        isAdmin={isAdmin}
        onImportComplete={() => {
          loadTopics();
          loadQuestions();
          setFeedbackMsg('Curriculum imported from document successfully without conflicts!');
        }}
      />
    </div>
  );
};

export default CurriculumManager;
